import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShoppingBag, Star, Filter, Sparkles, Gem, Crown, Shirt, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FashionPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const { user } = useAuth();
  const { addToCart } = useCart();

  const categories = [
    { id: 'all', name: 'All', icon: Sparkles },
    { id: 'ties', name: 'Ties', icon: Shirt },
    { id: 'headpieces', name: 'Headpieces', icon: Crown },
    { id: 'outfits', name: 'Outfits', icon: Shirt },
    { id: 'jewelry', name: 'Jewelry', icon: Gem },
  ];

  // Sample fashion products with earthy, woody, feminine imagery
  const sampleProducts = [
    { id: 'f-1', name: 'Silk Bow Tie - Forest', description: 'Handcrafted silk bow tie in deep forest green with natural texture', price: 89.99, category: 'ties', image_url: 'https://images.pexels.com/photos/45055/pexels-photo-45055.jpeg', in_stock: true, customizable: true },
    { id: 'f-2', name: 'Velvet Necktie - Bark', description: 'Luxurious velvet necktie in rich bark brown, inspired by nature', price: 129.99, category: 'ties', image_url: 'https://images.pexels.com/photos/1191531/pexels-photo-1191531.jpeg', in_stock: true, customizable: true },
    { id: 'f-3', name: 'Leaf Crown Headpiece', description: 'Elegant gold-leaf adorned headpiece with natural vine details', price: 299.99, category: 'headpieces', image_url: 'https://images.pexels.com/photos/1721932/pexels-photo-1721932.jpeg', in_stock: true, customizable: true },
    { id: 'f-4', name: 'Wooden Bead Tiara', description: 'Delicate tiara with hand-carved wooden beads and pearls', price: 249.99, category: 'headpieces', image_url: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg', in_stock: true, customizable: true },
    { id: 'f-5', name: 'Earth Goddess Gown', description: 'Flowing evening gown in earthy terracotta with botanical embroidery', price: 1299.99, category: 'outfits', image_url: 'https://images.pexels.com/photos/2220326/pexels-photo-2220326.jpeg', in_stock: true, customizable: true },
    { id: 'f-6', name: 'Woodland Tailored Suit', description: 'Three-piece suit in moss green with natural fiber blend', price: 899.99, category: 'outfits', image_url: 'https://images.pexels.com/photos/1040945/pexels-photo-1040945.jpeg', in_stock: true, customizable: true },
    { id: 'f-7', name: 'Amber Stone Necklace', description: 'Natural amber pendant on handwoven cord with wooden clasp', price: 189.99, category: 'jewelry', image_url: 'https://images.pexels.com/photos/1454171/pexels-photo-1454171.jpeg', in_stock: true, customizable: false },
    { id: 'f-8', name: 'Leaf Drop Earrings', description: 'Gold-plated leaf earrings with natural gemstone accents', price: 159.99, category: 'jewelry', image_url: 'https://images.pexels.com/photos/2735970/pexels-photo-2735970.jpeg', in_stock: true, customizable: false },
  ];

  useEffect(() => {
    fetchProducts();
  }, [selectedCategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const url = selectedCategory === 'all' 
        ? `${API}/products` 
        : `${API}/products?category=${selectedCategory}`;
      const response = await axios.get(url);
      // Use sample products if no products from API, or merge with API products
      const apiProducts = response.data || [];
      const allProducts = apiProducts.length > 0 ? apiProducts : sampleProducts;
      setProducts(selectedCategory === 'all' ? allProducts : allProducts.filter(p => p.category === selectedCategory));
    } catch (error) {
      // Fallback to sample products
      setProducts(selectedCategory === 'all' ? sampleProducts : sampleProducts.filter(p => p.category === selectedCategory));
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (productId) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }
    try {
      await addToCart(productId);
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const reviews = [
    { name: 'Emily R.', rating: 5, comment: 'The custom gown was absolutely stunning. Perfect fit and incredible attention to detail.', service: 'Custom Gown' },
    { name: 'Michael T.', rating: 5, comment: 'Best tailored suit I\'ve ever owned. The craftsmanship is exceptional.', service: 'Tailored Suit' }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      {/* Hero */}
      <section className="relative h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg"
            alt="ARTERYVAIL Fashion"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#3D2314]/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#8C7B6B] mb-4">ARTERYVAIL Fashion</p>
            <h1 className="font-serif text-5xl md:text-7xl text-[#F5F2EB] mb-6">
              Handcrafted Luxury, Tailored for You
            </h1>
            <p className="text-lg text-[#A3988F] leading-relaxed mb-8">
              Bespoke fashion pieces meticulously crafted to elevate your confidence and express your unique style.
            </p>
            <a href="#shop" className="btn-primary inline-flex items-center space-x-3" data-testid="fashion-shop-btn">
              <span>Shop Collection</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Brand Video Section */}
      <section className="section-padding bg-[#3D2314]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-[#FFCBA4] mb-4">Our Vision</p>
              <h2 className="font-serif text-4xl text-[#F5F2EB] mb-6">The ARTERYVAIL Fashion Story</h2>
              <p className="text-[#A3988F] leading-relaxed mb-6">
                Every piece in our collection tells a story of nature's elegance. Inspired by woodland textures, 
                bark patterns, and the organic beauty of leaves and trees, our artisans create wearable art.
              </p>
              <p className="text-[#A3988F] leading-relaxed mb-8">
                From earthy-toned ties to delicate leaf-inspired jewelry, each item is thoughtfully sourced 
                and handcrafted with reverence for nature's timeless beauty.
              </p>
              <Link to="/fashion" className="btn-primary inline-flex items-center space-x-3" data-testid="fashion-explore-btn">
                <span>Explore Collection</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="relative rounded-xl overflow-hidden aspect-video group cursor-pointer">
                <img
                  src="https://images.pexels.com/photos/1721932/pexels-photo-1721932.jpeg"
                  alt="ARTERYVAIL Fashion"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#0A0A0A]/40 flex items-center justify-center group-hover:bg-[#0A0A0A]/30 transition-colors">
                  <div className="w-20 h-20 bg-[#8C7B6B]/30 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-[#F5F2EB] ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-center">
                  <p className="text-xs text-[#A3988F] bg-black/50 px-3 py-1 rounded-full inline-block">
                    Video Coming Soon
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories & Products */}
      <section id="shop" className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#8C7B6B] mb-4">Our Collection</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F2EB]">Handcrafted Pieces</h2>
          </motion.div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-[#8C7B6B] text-[#F5F2EB]'
                    : 'bg-[#1A1514] text-[#A3988F] hover:bg-[#8C7B6B]/20'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                <span className="text-sm tracking-wider">{cat.name}</span>
              </button>
            ))}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="loader" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <Sparkles className="w-16 h-16 text-[#8C7B6B]/30 mx-auto mb-6" />
              <h3 className="font-serif text-2xl text-[#F5F2EB] mb-3">No Products Found</h3>
              <p className="text-[#A3988F]">Check back soon for new additions to our collection.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="luxury-card rounded-xl overflow-hidden group"
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {product.customizable && (
                      <span className="absolute top-4 left-4 bg-[#8C7B6B] text-[#F5F2EB] text-xs tracking-wider uppercase px-3 py-1 rounded-full">
                        Customizable
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-xs tracking-[0.2em] uppercase text-[#8C7B6B] mb-2">{product.category}</p>
                    <h3 className="font-serif text-lg text-[#F5F2EB] mb-2">{product.name}</h3>
                    <p className="text-[#A3988F] text-sm mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-semibold text-[#E5D3B3]">${product.price}</span>
                      <button
                        onClick={() => handleAddToCart(product.id)}
                        className="flex items-center space-x-2 text-sm text-[#F5F2EB] bg-[#261E1D] hover:bg-[#8C7B6B] px-4 py-2 rounded-full transition-colors"
                        data-testid={`add-to-cart-${product.id}`}
                      >
                        <ShoppingBag className="w-4 h-4" />
                        <span>Add</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Customization CTA */}
      <section className="section-padding bg-[#1A1514]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm tracking-[0.3em] uppercase text-[#8C7B6B] mb-4">Bespoke Service</p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#F5F2EB] mb-6">
                Luxury Customization
              </h2>
              <p className="text-[#A3988F] text-lg leading-relaxed mb-8">
                Can't find exactly what you're looking for? Our master craftsmen can create bespoke pieces 
                tailored specifically to your measurements, style preferences, and occasion needs. Each piece 
                draws inspiration from nature's organic beauty.
              </p>
              <ul className="space-y-4 mb-8">
                {['Personal consultation', 'Natural material selection', 'Custom measurements', 'Earthy tone palette options', 'Lifetime adjustments'].map((item) => (
                  <li key={item} className="flex items-center space-x-3 text-[#F5F2EB]">
                    <Sparkles className="w-4 h-4 text-[#8C7B6B]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/fashion/customize"
                className="btn-primary inline-flex items-center space-x-3"
                data-testid="fashion-customize-btn"
              >
                <span>Request Custom Order</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/3622614/pexels-photo-3622614.jpeg"
                alt="Custom Fashion"
                className="rounded-xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#8C7B6B] mb-4">Testimonials</p>
            <h2 className="font-serif text-4xl text-[#F5F2EB]">Client Stories</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="luxury-card rounded-xl p-8"
              >
                <div className="flex space-x-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${i < review.rating ? 'star-filled fill-current' : 'star-empty'}`}
                    />
                  ))}
                </div>
                <p className="text-[#F5F2EB] text-lg mb-6">"{review.comment}"</p>
                <div className="flex justify-between items-center">
                  <span className="text-[#8C7B6B] font-semibold">{review.name}</span>
                  <span className="text-sm text-[#A3988F]">{review.service}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FashionPage;
